'use client';

import { createContext, useContext, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

// eslint-disable-next-line no-console
console.log('[MOUNT] AnalyticsProvider');

// Create a safe analytics interface
interface AnalyticsContextType {
  trackEvent: (event: string, properties?: Record<string, any>) => void;
  trackPageView: (page: string, properties?: Record<string, any>) => void;
  identify: (userId: string, userProperties?: Record<string, any>) => void;
  setUserProperties: (properties: Record<string, any>) => void;
  reset: () => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | null>(null);

export function useAnalytics() {
  const context = useContext(AnalyticsContext);
  if (!context) {
    // Return a no-op implementation instead of throwing
    return {
      trackEvent: () => {},
      trackPageView: () => {},
      identify: () => {},
      setUserProperties: () => {},
      reset: () => {},
    };
  }
  return context;
}

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const pathname = usePathname();
  const previousPathname = useRef<string>('');

  // Safe analytics implementation
  const safeAnalytics = {
    trackEvent: (event: string, properties?: Record<string, any>) => {
      try {
        // Only try to use PostHog if it's available
        if (typeof window !== 'undefined' && (window as any).posthog) {
          (window as any).posthog.capture(event, properties);
        }
        if (process.env.NODE_ENV === 'development') {
          console.log('📊 Event:', event, properties);
        }
      } catch (error) {
        console.warn('Analytics error:', error);
      }
    },
    trackPageView: (page: string, properties?: Record<string, any>) => {
      try {
        if (typeof window !== 'undefined' && (window as any).posthog) {
          (window as any).posthog.capture('page_view', { page, ...properties });
        }
        if (process.env.NODE_ENV === 'development') {
          console.log('📊 Page View:', page, properties);
        }
      } catch (error) {
        console.warn('Analytics error:', error);
      }
    },
    identify: (userId: string, userProperties?: Record<string, any>) => {
      try {
        if (typeof window !== 'undefined' && (window as any).posthog) {
          (window as any).posthog.identify(userId, userProperties);
        }
      } catch (error) {
        console.warn('Analytics error:', error);
      }
    },
    setUserProperties: (properties: Record<string, any>) => {
      try {
        if (typeof window !== 'undefined' && (window as any).posthog) {
          (window as any).posthog.people.set(properties);
        }
      } catch (error) {
        console.warn('Analytics error:', error);
      }
    },
    reset: () => {
      try {
        if (typeof window !== 'undefined' && (window as any).posthog) {
          (window as any).posthog.reset();
        }
      } catch (error) {
        console.warn('Analytics error:', error);
      }
    },
  };

  // Initialize PostHog safely
  useEffect(() => {
    try {
      const POSTHOG_API_KEY = process.env.NEXT_PUBLIC_POSTHOG_API_KEY;
      const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://app.posthog.com';

      if (POSTHOG_API_KEY && typeof window !== 'undefined') {
        // Dynamically import PostHog to avoid SSR issues
        import('posthog-js').then((posthog) => {
          posthog.default.init(POSTHOG_API_KEY, {
            api_host: POSTHOG_HOST,
            loaded: (posthogInstance) => {
              if (process.env.NODE_ENV === 'development') {
                posthogInstance.debug();
              }
            },
            capture_pageview: false,
            capture_pageleave: true,
            autocapture: false,
            disable_session_recording: true,
            opt_out_capturing_by_default: false,
            respect_dnt: true,
            mask_all_text: false,
            advanced_disable_decide: true,
          });
          
          if (process.env.NODE_ENV === 'development') {
            console.log('📊 Analytics initialized (PostHog)');
          }
        }).catch((error) => {
          console.warn('Failed to initialize PostHog:', error);
        });
      } else if (process.env.NODE_ENV === 'development') {
        console.log('📊 Analytics initialized (Development mode - events logged to console)');
      }
    } catch (error) {
      console.warn('Analytics initialization error:', error);
    }
  }, []);

  // Track page views when pathname changes
  useEffect(() => {
    if (pathname && pathname !== previousPathname.current) {
      safeAnalytics.trackPageView(pathname, {
        previous_page: previousPathname.current || 'direct',
        referrer: typeof document !== 'undefined' ? document.referrer || 'direct' : 'direct',
      });
      previousPathname.current = pathname;
    }
  }, [pathname]);

  return (
    <AnalyticsContext.Provider value={safeAnalytics}>
      {children}
    </AnalyticsContext.Provider>
  );
} 